import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-responsive-modal';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import { closeIconPath } from 'containers/modals/ModalRoot';
import { authHeaders } from 'actions';
import Routes from 'utils/routes.js.erb';

const RequestMergeModal = ({ open, accountId, sourcePlayer, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const candidates = useQuery({
    queryKey: ['administration', 'accounts', accountId, 'mergeCandidates', sourcePlayer?.id, debouncedSearch],
    queryFn: ({ signal }) => axios.get(
      Routes.merge_candidates_api_dashboard_administration_account_path(accountId, {
        source_player_id: sourcePlayer.id,
        search: debouncedSearch,
      }),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
    enabled: open && !!sourcePlayer && debouncedSearch.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

  const close = () => {
    setSearch('');
    setDebouncedSearch('');
    setSelected(null);
    onClose();
  };

  const confirm = () => {
    if (!selected) return;
    onConfirm(sourcePlayer.id, selected.id);
    close();
  };

  const records = candidates.data ?? [];

  // Single source of truth for the results area. `search` (immediate) decides
  // hint-vs-active so the hint never flickers back once the user starts typing;
  // the query state decides results/searching/empty within the active view. The
  // debounce hasn't caught up until search === debouncedSearch, so treat that gap
  // as "searching" too — otherwise it would briefly fall through to "empty".
  const debouncePending = search !== debouncedSearch;
  let viewState;
  if (search.trim().length < 2) {
    viewState = 'hint';
  } else if (records.length > 0) {
    viewState = 'results';
  } else if (candidates.isFetching || debouncePending) {
    viewState = 'searching';
  } else {
    viewState = 'empty';
  }

  return (
    <Modal
      classNames={{ overlay: 'o-modal__wrap', modal: 'o-modal o-merge-modal' }}
      closeIconSvgPath={closeIconPath()}
      closeOnOverlayClick={true}
      open={open}
      onClose={close}
      center
    >
      <h3 className='o-merge-modal__title'><b>{t('administration.families.form.requestToMerge')}</b></h3>

      <div className='o-merge-modal__conditions'>
        <p className='o-merge-modal__conditions-title'>
          <i className='fa fa-info-circle'></i>&nbsp;{t('administration.families.mergeModal.conditionsTitle')}
        </p>
        <ul className='o-merge-modal__conditions-list'>
          <li>{t('administration.families.mergeModal.conditionBirthday')}</li>
          <li>{t('administration.families.mergeModal.conditionPrematurity')}</li>
        </ul>
        <p className='o-merge-modal__conditions-note'>{t('administration.families.mergeModal.conditionsNote')}</p>
      </div>

      <div className='o-merge-modal__section'>
        <span className='o-merge-modal__label u-txt--label'>
          {t('administration.families.mergeModal.sourceLabel')}
        </span>
        <div className='o-merge-modal__source'>
          <b>{sourcePlayer?.name}</b>
        </div>
      </div>

      <div className='o-merge-modal__section'>
        <span className='o-merge-modal__label u-txt--label'>
          {t('administration.families.mergeModal.targetLabel')}
        </span>
        <input
          type='text'
          className='o-merge-modal__search'
          value={search}
          placeholder={t('administration.families.mergeModal.searchPlaceholder')}
          onChange={(event) => { setSearch(event.target.value); setSelected(null); }}
        />

        <div className='o-merge-modal__results-area'>
          {viewState === 'results' && (
            <ul className='o-merge-modal__results'>
              {records.map(player => (
                <li
                  key={player.id}
                  className={clsx('o-merge-modal__result', { 'o-merge-modal__result--selected': selected && selected.id === player.id })}
                  onClick={() => setSelected(player)}
                >
                  <b className='o-merge-modal__result-name'>{player.name}</b>
                  <span className='u-txt--label'>{player.account_name} — {player.account_email}</span>
                </li>
              ))}
            </ul>
          )}
          {viewState === 'hint' && (
            <div className='o-merge-modal__empty'>
              <i className='fa fa-search o-merge-modal__empty-icon'></i>
              <p className='u-txt--label'>{t('administration.families.mergeModal.searchHint')}</p>
            </div>
          )}
          {viewState === 'searching' && (
            <div className='o-merge-modal__status'>
              <span className='u-txt--label'>{t('administration.families.mergeModal.searching')}</span>
            </div>
          )}
          {viewState === 'empty' && (
            <div className='o-merge-modal__empty'>
              <i className='fa fa-user-times o-merge-modal__empty-icon'></i>
              <p className='o-merge-modal__empty-title'>{t('administration.families.mergeModal.noResultsTitle')}</p>
              <p className='u-txt--label'>{t('administration.families.mergeModal.noResults')}</p>
            </div>
          )}
        </div>
      </div>

      <p className='o-merge-modal__warning'>
        <i className='fa fa-exclamation-triangle'></i>&nbsp;{t('administration.families.mergeModal.warning')}
      </p>

      <div className='o-merge-modal__actions'>
        <span
          className={clsx('o-sp-button o-sp-button--update o-sp-button--primary', { 'o-sp-button--disabled': !selected })}
          onClick={confirm}
        >
          {t('administration.families.mergeModal.confirm')}
        </span>
      </div>
    </Modal>
  );
};

RequestMergeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sourcePlayer: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default RequestMergeModal;
